import React, { useState } from "react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { OptionList } from "../profile/ProfileParts";
import {
  Dumbbell,
  Info,
  LibraryBig,
  MoreVertical,
  Pencil,
  Plus,
  RotateCcw,
  Share2,
  Trash2,
  Repeat,
  Users,
} from "lucide-react";
import ShareLinkDialog from "../share/ShareLinkDialog";
import DetailSheet from "../session/generate/DetailSheet";
import HelpLink from "../help/HelpLink";
import {
  PROGRESSION_STYLE_LABELS,
  programmeDeloadWeeks,
  formatWeekList,
  getFramework,
} from "../../config/periodisation";
import {
  programmeMinutesRange,
  formatMinutesRange,
} from "../../config/programmePlanner";
import {
  displayFocus,
  formatSessionDate,
  programmeStart,
} from "../../utils/programmeSchedule";

const SPLIT_LABELS = {
  full_body: "Full Body",
  upper_lower: "Upper / Lower",
  ppl: "Push / Pull / Legs",
  body_part: "Body part split",
};

// "Sam", "Sam and Alex", "Sam, Alex and Jo".
const listNames = (names) =>
  names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

// "Daily Undulating (DUP)" reads as noise on one line; drop the bracket.
const shortFramework = (label) => (label || "").replace(/\s*\(.*\)\s*$/, "");

const Fact = ({ label, value }) => (
  <div className="rounded-lg border border-border bg-background px-2.5 py-2">
    <div className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
      {label}
    </div>
    <div className="text-sm font-semibold text-foreground">{value}</div>
  </div>
);

const ProgrammeHeader = ({
  programme,
  currentWeek,
  stats,
  onDeleteProgramme,
  onResetWeek,
  onRepeatLastWeek,
  onOpenLibrary,
  onEditProgramme,
  onNewProgramme,
  onTrainTogether,
  onSetEquipment,
  group = null,
}) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  // Which of your equipment sets this programme is built for (#196).
  const { equipmentPresets = [] } = useUserProfile();
  const defaultPreset = equipmentPresets.find((p) => p.is_default);
  const programmePreset = equipmentPresets.find((p) => p.id === programme.equipment_preset_id);
  const kitList = (preset) => {
    const items = Array.isArray(preset?.equipment) ? preset.equipment : [];
    return items.length > 4 ? `${items.slice(0, 4).join(", ")} +${items.length - 4}` : items.join(", ");
  };
  const equipmentName = programmePreset?.name || "Profile default";
  const [resetOpen, setResetOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  const framework = getFramework(programme.progression_style);
  const frameworkLabel =
    framework?.label ||
    PROGRESSION_STYLE_LABELS[programme.progression_style] ||
    programme.progression_style;
  const splitLabel = SPLIT_LABELS[programme.split] || programme.split;
  const focus = displayFocus(programme.focus);
  const deloads = programmeDeloadWeeks(programme);
  // Each day has its own length since #92: show the range, e.g. "30–45 min".
  const minutesText = formatMinutesRange(programmeMinutesRange(programme));
  const upcomingDeload = deloads.find((w) => w >= currentWeek);
  const imported = programme.source === "imported";
  const metaLine = [
    imported ? "Imported, run as written" : shortFramework(frameworkLabel),
    imported ? null : splitLabel,
    `${programme.days_per_week} a week`,
    minutesText,
  ]
    .filter(Boolean)
    .join(" · ");
  const others = (group?.mates || []).filter((m) => !m.me && m.name);
  const ownerName = (group?.mates || []).find((m) => m.owner && !m.me)?.name?.split(" ")[0] || null;
  const pct = (n) => (stats.total > 0 ? (n / stats.total) * 100 : 0);
  // Week 1's Monday (#92); moves when a week is repeated.
  const startDate = programmeStart(programme);
  const started = startDate
    ? startDate.toLocaleDateString("en-AU", { day: "numeric", month: "short" })
    : formatSessionDate(programme.created_at);

  // #52: close the confirm dialog FIRST and start the delete on the next
  // frame. Deleting in the same commit tears down the open Radix layers
  // (dropdown → alert-dialog) with this whole component when the programme goes
  // null, which can leave `pointer-events: none` stuck on <body>.
  const handleConfirmDelete = () => {
    setDeleteOpen(false);
    requestAnimationFrame(() => onDeleteProgramme());
  };

  const handleConfirmReset = () => {
    setResetOpen(false);
    requestAnimationFrame(() => onResetWeek?.(currentWeek));
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-3">
      {/* Title wraps rather than truncating, and nothing sits beside it but
          the menu, so a long name can never run into the week badge. */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 gap-y-0.5">
        <h2 className="text-[22px] font-bold leading-tight text-white [text-wrap:balance]">
          {programme.title}
        </h2>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              aria-label="Programme options"
              className="h-9 w-9 border-border bg-card"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {onEditProgramme && (
              <DropdownMenuItem onClick={onEditProgramme}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit programme
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onOpenLibrary}>
              <LibraryBig className="mr-2 h-4 w-4" />
              My programmes
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onNewProgramme}>
              <Plus className="mr-2 h-4 w-4" />
              New programme
            </DropdownMenuItem>
            {onSetEquipment && (
              <DropdownMenuItem onClick={() => setEquipmentOpen(true)}>
                <Dumbbell className="mr-2 h-4 w-4" />
                Equipment
              </DropdownMenuItem>
            )}
            {onTrainTogether && (
              <DropdownMenuItem onClick={onTrainTogether}>
                <Users className="mr-2 h-4 w-4" />
                Train together
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setShareOpen(true)}>
              <Share2 className="mr-2 h-4 w-4" />
              Share a copy
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {onRepeatLastWeek && (
              // Deferred a frame so the menu's teardown finishes before the
              // programme object is replaced (#72).
              <DropdownMenuItem onClick={() => requestAnimationFrame(() => onRepeatLastWeek())}>
                <Repeat className="mr-2 h-4 w-4" />
                Repeat last week
              </DropdownMenuItem>
            )}
            {onResetWeek && (
              <DropdownMenuItem onClick={() => setResetOpen(true)}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset week {currentWeek}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete programme
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <p className="col-span-2 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">
            Week {currentWeek} of {programme.duration_weeks}
          </span>
          {focus && ` · ${focus}`}
        </p>
      </div>

      {/* Who's training it with you (#184). */}
      {others.length > 0 && (
        <button
          type="button"
          onClick={onTrainTogether}
          className="flex w-full items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-left hover:bg-muted/40"
        >
          <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">
            {`With ${listNames(others.map((m) => m.name.split(" ")[0]))}`}
            {group.follows && ownerName && (
              <span className="text-muted-foreground">{` · following ${ownerName}'s version`}</span>
            )}
          </span>
        </button>
      )}

      {/* One line of structure; the detail is a tap away. */}
      <button
        type="button"
        onClick={() => setAboutOpen(true)}
        className="flex w-full items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-left hover:bg-muted/40"
      >
        <span className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground">
          {metaLine}
        </span>
        <Info className="h-4 w-4 shrink-0 text-muted-foreground/70" />
      </button>

      <div className="grid gap-1.5">
        <div
          className="flex h-1.5 overflow-hidden rounded-full bg-white/[0.06]"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={stats.total}
          aria-valuenow={stats.completed}
          aria-label="Sessions done"
        >
          <span className="h-full bg-secondary" style={{ width: `${pct(stats.completed)}%` }} />
          <span className="h-full bg-white/20" style={{ width: `${pct(stats.missed)}%` }} />
        </div>
        <div className="flex justify-between gap-2 text-xs text-muted-foreground">
          <span>
            {stats.completed} of {stats.total} done
            {stats.missed > 0 && ` · ${stats.missed} missed`}
          </span>
          {upcomingDeload && <span>Deload in week {upcomingDeload}</span>}
        </div>
      </div>

      <DetailSheet
        open={aboutOpen}
        onOpenChange={setAboutOpen}
        title="About this programme"
        description={[focus, `${programme.duration_weeks} weeks`, started && `started ${started}`]
          .filter(Boolean)
          .join(" · ")}
        showDescription
        footer={
          <div className="grid gap-2">
            {onEditProgramme && (
              <Button
                type="button"
                variant="outline"
                className="h-12 w-full border-border bg-muted/40 text-base font-semibold hover:bg-muted/60"
                onClick={() => {
                  setAboutOpen(false);
                  onEditProgramme();
                }}
              >
                Edit programme
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              className="h-12 w-full border-border bg-muted/40 text-base font-semibold hover:bg-muted/60"
              onClick={() => setAboutOpen(false)}
            >
              Done
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-2 gap-2">
          <Fact label="Structure" value={imported ? "Imported" : shortFramework(frameworkLabel)} />
          {imported ? (
            <Fact label="From" value={programme.source_meta?.file || "Your own programme"} />
          ) : (
            <Fact label="Split" value={splitLabel} />
          )}
          <Fact label="Sessions" value={`${programme.days_per_week} a week`} />
          {minutesText && <Fact label="Length" value={minutesText} />}
          <Fact label="Equipment" value={equipmentName} />
        </div>
        {imported && (
          <p className="text-sm leading-relaxed text-muted-foreground">
            Imported from a programme you already had. Khronos runs each session exactly as written and never changes
            or re-plans them. You can still edit a session yourself.
          </p>
        )}
        {!imported && framework?.description && (
          <p className="text-sm leading-relaxed text-muted-foreground">
            {framework.description}{" "}
            <HelpLink topic="programmes#structures" label="About programme structures" />
          </p>
        )}
        {deloads.length > 0 && (
          <p className="text-sm leading-relaxed text-muted-foreground">
            {deloads.length > 1 ? "Weeks" : "Week"} {formatWeekList(deloads)}{" "}
            {deloads.length > 1 ? "are lighter deload weeks" : "is a lighter deload week"}, so
            you recover and absorb the training.{" "}
            <HelpLink topic="programmes#deload-weeks" label="About deload weeks" />
          </p>
        )}
        {programme.notes && (
          <div className="space-y-1">
            <p className="text-xs font-semibold text-muted-foreground">
              Your goals and limits
            </p>
            <p className="text-sm text-foreground">{programme.notes}</p>
          </div>
        )}
      </DetailSheet>

      {onSetEquipment && (
        <DetailSheet
          open={equipmentOpen}
          onOpenChange={setEquipmentOpen}
          title="Equipment"
          description="Sessions built for this programme use this kit."
          showDescription
        >
          <OptionList
            value={programmePreset ? programmePreset.id : "default"}
            onChange={(v) => onSetEquipment(v === "default" ? null : v)}
            options={[
              {
                value: "default",
                label: "Profile default",
                description: defaultPreset
                  ? `${defaultPreset.name}: ${kitList(defaultPreset)}`
                  : "The equipment in your Profile",
              },
              ...equipmentPresets.map((p) => ({
                value: p.id,
                label: p.name,
                description: kitList(p) || "No equipment",
              })),
            ]}
          />
          {equipmentPresets.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Add equipment sets in your Profile to pick one here.
            </p>
          )}
        </DetailSheet>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete programme?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &ldquo;{programme.title}&rdquo; and all
              its session assignments. Completed workout history will be
              preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset-week confirmation (#59) */}
      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset week {currentWeek}?</AlertDialogTitle>
            <AlertDialogDescription>
              This discards your edits and reordering for week {currentWeek} and
              restores its original skeleton from the programme framework. All
              slots in the week become empty again. Completed workout history is
              preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmReset}>
              Reset week
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Shares the structure and any planned sessions. The recipient's copy
          lands paused in their library with none of this programme's progress
          on it. */}
      <ShareLinkDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        request={{ kind: "programme", programme_id: programme.id }}
        defaultTitle={programme.title || ""}
      />
    </div>
  );
};

export default ProgrammeHeader;
